export type Paged<T> = {
  items: T[];
  total: number;
  page: number;
  page_size: number;
};

export type OverviewStats = Record<string, any>;

export type SummariesMap = Record<string, any>;

export type PercentagesMap = Record<string, number>;

export type TopReposBySeverityRow = {
  repo: string;
  total_cves: number;
} & Record<string, any>;

export type ImagesPerVersionPoint = {
  version: string;
  image_count: number;
} & Record<string, any>;

export type CachedHomePayload = {
  overview: OverviewStats;
  summaries?: SummariesMap;
  summaries_top10?: SummariesMap;
  percentages?: PercentagesMap;
} & Record<string, any>;

export type Level1RepoBundle = {
  repo_prefix: string;
  cve_percentages: Record<string, number>;
  persist_obsolete: {
    [severity: string]: { Persisting: number; Obsolete: number };
  };
  top_vulnerable: Array<{
    image: string;
    version: string;
    total_vulns: number;
    creation_time?: string | null;
  }>;
  top_chains: Array<{
    image: string;
    version: string;
    total_chains: number;
  }>;
  oldest_newest: Array<{
    type: "oldest" | "newest";
    version: string;
    creation_time?: string | null;
  }>;
  versions?: any[];
};

const DEV_BACKEND = `${window.location.protocol}//${window.location.hostname}:8000`;
const PROD_BACKEND = "https://janus-website.onrender.com";
const QS_API =
  new URLSearchParams(window.location.search).get("api") || undefined;

export const API_BASE_URL = (
  (QS_API as string | undefined) ||
  (process.env.REACT_APP_API_BASE as string | undefined) ||
  (process.env.NODE_ENV === "development" ? DEV_BACKEND : PROD_BACKEND)
).replace(/\/+$/, "");

(() => {
  const pageOrigin = window.location.origin.toLowerCase();
  const apiOrigin = API_BASE_URL.toLowerCase();
  const mix =
    (pageOrigin.includes("localhost") && apiOrigin.includes("127.0.0.1")) ||
    (pageOrigin.includes("127.0.0.1") && apiOrigin.includes("localhost"));
  if (mix) {
    console.warn(
      `[API] Warning: You are mixing localhost and 127.0.0.1.\n` +
        `Page: ${pageOrigin}\nAPI:  ${apiOrigin}\nUse the same host label.`
    );
  }
})();

type ReqOpts = RequestInit & {
  timeoutMs?: number;
  retries?: number;
  backoffBaseMs?: number;
  cachedPath?: string;
};

function sleep(ms: number) {
  return new Promise((r) => setTimeout(r, ms));
}

async function request<T = unknown>(
  path: string,
  options: ReqOpts = {}
): Promise<T> {
  const base = (api as any).__base ?? API_BASE_URL;
  const url = `${base}${path}`;
  const {
    timeoutMs = 45000,
    retries = 2,
    backoffBaseMs = 300,
    cachedPath,
    ...fetchOpts
  } = options;

  const hasBody = fetchOpts.body !== undefined && fetchOpts.body !== null;

  const headers: Record<string, string> = {
    Accept: "application/json",
    ...(hasBody ? { "Content-Type": "application/json" } : {}),
    ...((fetchOpts.headers as any) || {}),
  };

  let lastErr: any;

  for (let attempt = 0; attempt <= retries; attempt++) {
    const ctrl = new AbortController();
    const signal = (fetchOpts as any).signal ?? ctrl.signal;
    const timer = setTimeout(() => ctrl.abort("timeout"), timeoutMs);

    try {
      const res = await fetch(url, {
        ...fetchOpts,
        headers,
        signal,
        mode: "cors",
      });

      clearTimeout(timer);

      if (res.ok) {
        if (res.status === 204) return null as T;
        const ct = res.headers.get("content-type") || "";
        if (ct.includes("application/json")) {
          return (await res.json()) as T;
        }
        const txt = await res.text();
        return JSON.parse(txt) as T;
      }

      if ([429, 500, 502, 503, 504].includes(res.status) && attempt < retries) {
        const jitter = Math.random() * 100;
        await sleep(Math.pow(3, attempt) * backoffBaseMs + jitter);
        continue;
      }

      const text = await res.text().catch(() => "");
      throw new Error(`${res.status} ${res.statusText} – ${text}`);
    } catch (e: any) {
      clearTimeout(timer);

      const msg = String(e?.message || "").toLowerCase();
      const isAbort =
        e?.name === "AbortError" ||
        msg.includes("timeout") ||
        msg.includes("aborted") ||
        msg.includes("canceled") ||
        msg.includes("cancelled");

      if (isAbort) {
        if (attempt < retries) {
          const jitter = Math.random() * 100;
          await sleep(Math.pow(3, attempt) * backoffBaseMs + jitter);
          continue;
        }
        throw {
          code: "ABORTED_IGNORE",
          name: "AbortError",
          message: "Request aborted or timed out",
        };
      }

      lastErr = e;

      if (attempt < retries) {
        const jitter = Math.random() * 100;
        await sleep(Math.pow(3, attempt) * backoffBaseMs + jitter);
        continue;
      }

      if (cachedPath) {
        try {
          const base2 = (api as any).__base ?? API_BASE_URL;
          const cachedUrl = `${base2}${cachedPath}`;
          const cres = await fetch(cachedUrl, {
            headers: { Accept: "application/json" },
            mode: "cors",
          });
          if (cres.ok) {
            return (await cres.json()) as T;
          }
        } catch {
        }
      }

      throw e;
    }
  }

  throw lastErr;
}

export const api = {
  level0: {
    list: () => request<any[]>("/level0"),
    categories: () => request<string[]>("/level0/categories"),
    names: () => request<string[]>("/level0/names"),
    byId: (id: string) => request<any>(`/level0/${encodeURIComponent(id)}`),
    byImage: (image: string) =>
      request<any>(`/level0/${encodeURIComponent(image)}`),
    create: (payload: any) =>
      request<any>("/level0", { method: "POST", body: JSON.stringify(payload) }),
    update: (id: string, payload: any) =>
      request<any>(`/level0/${encodeURIComponent(id)}`, {
        method: "PUT",
        body: JSON.stringify(payload),
      }),
    remove: (id: string) =>
      request<null>(`/level0/${encodeURIComponent(id)}`, {
        method: "DELETE",
      }),
    listPaged: <T = any>(page: number, pageSize: number, q?: string) =>
      request<Paged<T>>(
        `/level0?page=${page}&page_size=${pageSize}${
          q ? `&q=${encodeURIComponent(q)}` : ""
        }`,
        { timeoutMs: 45000 }
      ),
  },

  level1: {
    list: () => request<any[]>("/level1"),
    forRepo: (repoPrefix: string) =>
      request<any[]>(`/level1/repo/${encodeURIComponent(repoPrefix)}`),
    summary: (image: string) =>
      request<any>(`/level1/${encodeURIComponent(image)}`),
    table: (opts: {
      repo?: string;
      q?: string;
      page?: number;
      page_size?: number;
    }) => {
      const qs = new URLSearchParams();
      if (opts?.repo) qs.set("repo", opts.repo);
      if (opts?.q) qs.set("q", opts.q);
      qs.set("page", String(opts?.page ?? 1));
      qs.set("page_size", String(opts?.page_size ?? 10));
      return request<Paged<any>>(`/level1/table?${qs.toString()}`);
    },
  },

  level2: {
    byVersionChained: (ver: string) =>
      request<any[]>(`/level2/version/${encodeURIComponent(ver)}/chains`),

    byVersion: (ver: string) =>
      request<any[]>(`/level2/version/${encodeURIComponent(ver)}`),

    byImage: (image: string) =>
      request<any[]>(`/level2/image/${encodeURIComponent(image)}`),

    options: (imageId: string) =>
      request<any>(`/level2/image/${encodeURIComponent(imageId)}/options`),

    bundleByImage: (
      imageId: string,
      page = 1,
      pageSize = 25,
      f?: { severity?: string[]; pkg?: string[]; layer?: number | ""; q?: string }
    ) => {
      const qs = new URLSearchParams({
        page: String(page),
        page_size: String(pageSize),
      });
      if (f?.severity) f.severity.forEach((s) => qs.append("severity", s));
      if (f?.pkg) f.pkg.forEach((p) => qs.append("package", p));
      if (f?.layer !== undefined && f?.layer !== "")
        qs.set("layer", String(f.layer));
      if (f?.q) qs.set("q", f.q);
      return request(
        `/level2/image/${encodeURIComponent(imageId)}/bundle?${qs.toString()}`,
        {
          timeoutMs: 45000,
        }
      );
    },

    allPaged: (
      page = 1,
      pageSize = 25,
      f?: { severity?: string[]; pkg?: string[]; layer?: number | ""; q?: string }
    ) => {
      const qs = new URLSearchParams({
        page: String(page),
        page_size: String(pageSize),
      });
      if (f?.severity) f.severity.forEach((s) => qs.append("severity", s));
      if (f?.pkg) f.pkg.forEach((p) => qs.append("package", p));
      if (f?.layer !== undefined && f?.layer !== "")
        qs.set("layer", String(f.layer));
      if (f?.q) qs.set("q", f.q);
      return request(`/level2/paged?${qs.toString()}`, {
        timeoutMs: 45000,
        retries: 3,
      });
    },

    list: () => request<any[]>("/level2"),

    byPackage: (pkg: string) =>
      request<any>(`/level2/package/${encodeURIComponent(pkg)}`),

    byVersionInstalled: (ver: string) =>
      request<any[]>(`/level2/version_installed/${encodeURIComponent(ver)}`),

    create: (payload: any) =>
      request<any>("/level2", { method: "POST", body: JSON.stringify(payload) }),

    update: (pkg: string, payload: any) =>
      request<any>(`/level2/package/${encodeURIComponent(pkg)}`, {
        method: "PUT",
        body: JSON.stringify(payload),
      }),

    remove: (pkg: string) =>
      request<null>(`/level2/package/${encodeURIComponent(pkg)}`, {
        method: "DELETE",
      }),

    byImagePaged: (
      image: string,
      page = 1,
      pageSize = 25,
      f?: { severity?: string[]; pkg?: string[]; layer?: number | ""; q?: string }
    ) => {
      const qs = new URLSearchParams({
        page: String(page),
        page_size: String(pageSize),
      });
      if (f?.severity) f.severity.forEach((s) => qs.append("severity", s));
      if (f?.pkg) f.pkg.forEach((p) => qs.append("package", p));
      if (f?.layer !== undefined && f?.layer !== "")
        qs.set("layer", String(f.layer));
      if (f?.q) qs.set("q", f.q);
      return request(
        `/level2/image/${encodeURIComponent(image)}/paged?${qs.toString()}`,
        {
          timeoutMs: 45000,
        }
      );
    },

    byVersionPaged: (
      ver: string,
      page = 1,
      pageSize = 25,
      f?: { severity?: string[]; pkg?: string[]; layer?: number | ""; q?: string }
    ) => {
      const qs = new URLSearchParams({
        page: String(page),
        page_size: String(pageSize),
      });
      if (f?.severity) f.severity.forEach((s) => qs.append("severity", s));
      if (f?.pkg) f.pkg.forEach((p) => qs.append("package", p));
      if (f?.layer !== undefined && f?.layer !== "")
        qs.set("layer", String(f.layer));
      if (f?.q) qs.set("q", f.q);
      return request(
        `/level2/version/${encodeURIComponent(ver)}/paged?${qs.toString()}`,
        {
          timeoutMs: 45000,
        }
      );
    },
  },

  stats: {
    summaries: () =>
      request<SummariesMap>("/statistics/cached/summaries", {
        timeoutMs: 45000,
        retries: 2,
      }),

    cveBreakdown: () =>
      request<Record<string, number>>("/statistics/cve-breakdown", {
        timeoutMs: 45000,
        retries: 2,
      }),

    repoCount: () =>
      request<number>("/statistics/repo-counts", {
        timeoutMs: 45000,
        retries: 2,
      }),

    percentage: () =>
      request<PercentagesMap>("/statistics/cached/percentages", {
        timeoutMs: 45000,
        retries: 2,
      }),

    largestImages: () =>
      request<any[]>("/statistics/largest_images", {
        timeoutMs: 45000,
        retries: 1,
      }),

    recentlyUpdated: () =>
      request<any[]>("/statistics/recently_updated", {
        timeoutMs: 45000,
        retries: 1,
      }),

    overview: () =>
      request<OverviewStats>("/statistics/cached/overview", {
        timeoutMs: 45000,
        retries: 2,
      }),

    mostPopular: () =>
      request<any[]>("/statistics/most_popular", {
        timeoutMs: 45000,
        retries: 1,
      }),

    mostPopularForRepo: (repo: string) =>
      request<any[]>(
        `/statistics/most_popular/${encodeURIComponent(repo)}`,
        {
          timeoutMs: 45000,
          retries: 1,
        }
      ),

    click: (imageName: string) =>
      request<{ message: string }>(
        `/statistics/click/${encodeURIComponent(imageName)}`,
        {
          method: "POST",
          timeoutMs: 15000,
          retries: 0,
        }
      ),

    cveBreakdownForRepo: (repoPrefix: string) =>
      request<Record<string, number>>(
        `/statistics/cve_repo/${encodeURIComponent(repoPrefix)}`,
        {
          timeoutMs: 45000,
          retries: 1,
        }
      ),

    persistingVsObsolete: (repoPrefix: string) =>
      request<any>(
        `/statistics/persisting_vs_obsolete/${encodeURIComponent(
          repoPrefix
        )}`,
        {
          timeoutMs: 45000,
          retries: 1,
        }
      ),

    topVulnerableVersions: (repoPrefix: string) =>
      request<any[]>(
        `/statistics/top_vulnerable_versions/${encodeURIComponent(
          repoPrefix
        )}`,
        {
          timeoutMs: 45000,
          retries: 1,
        }
      ),

    oldestNewestVersions: (repoPrefix: string) =>
      request<any[]>(
        `/statistics/oldest_newest_versions/${encodeURIComponent(
          repoPrefix
        )}`,
        {
          timeoutMs: 45000,
          retries: 1,
        }
      ),

    topChainVersions: (repoPrefix: string) =>
      request<any[]>(
        `/statistics/top_chain_versions/${encodeURIComponent(repoPrefix)}`,
        {
          timeoutMs: 45000,
          retries: 1,
        }
      ),

    totalVulnerabilities: (image: string) =>
      request<{ image: string; total_vulnerabilities: number }>(
        `/statistics/vulnerabilities/${encodeURIComponent(image)}`,
        { timeoutMs: 45000, retries: 1 }
      ),

    uniquePackages: (image: string) =>
      request<{ unique_vulnerable_packages: number }>(
        `/statistics/unique_packages/${encodeURIComponent(image)}`,
        { timeoutMs: 45000, retries: 1 }
      ),

    totalLayers: (image: string) =>
      request<{ total_layers: number }>(
        `/statistics/total_layers/${encodeURIComponent(image)}`,
        { timeoutMs: 45000, retries: 1 }
      ),

    severityByImage: (image: string) =>
      request<Record<string, number>>(
        `/statistics/severity_breakdown/${encodeURIComponent(image)}`,
        { timeoutMs: 45000, retries: 1 }
      ),

    level2Bundle: (image: string) =>
      request<{
        image: string;
        total_vulnerabilities: number;
        unique_vulnerable_packages: number;
        total_layers: number;
        severity_map: Record<string, number>;
      }>(`/statistics/cached/level2/${encodeURIComponent(image)}`, {
        timeoutMs: 45000,
        retries: 1,
      }),

    topReposBySeverity: (severity: string) =>
      request<TopReposBySeverityRow[]>(
        `/statistics/top_repos_by_severity/${encodeURIComponent(severity)}`,
        { timeoutMs: 45000, retries: 1 }
      ),

    topLevel2BySeverity: (image: string, severity: string) =>
      request<Array<{ name: string; count: number }>>(
        `/statistics/top_level2_by_severity?image=${encodeURIComponent(
          image
        )}&severity=${encodeURIComponent(severity)}`,
        { timeoutMs: 45000, retries: 1 }
      ),

    imagesPerVersion: (limit: number = 25) =>
      request<ImagesPerVersionPoint[]>(
        `/statistics/images_per_version?limit=${limit}`,
        { timeoutMs: 45000, retries: 1 }
      ),

    level1Bundle: (repoPrefix: string) =>
      request<Level1RepoBundle>(
        `/statistics/cached/level1/${encodeURIComponent(repoPrefix)}`,
        {
          timeoutMs: 45000,
          retries: 1,
        }
      ),

    chainsByRepo: () =>
      request<Array<{ repo: string; total_chains: number }>>(
        "/statistics/chains_by_repo",
        { timeoutMs: 45000, retries: 1 }
      ),

    cvesByCategory: () =>
      request<Array<{ category: string; total_cves: number }>>(
        "/statistics/cves_by_category",
        { timeoutMs: 45000, retries: 1 }
      ),

    level0TopPopular: () =>
      request<any[]>("/statistics/level0/top_popular", {
        timeoutMs: 45000,
        retries: 1,
      }),

    level0RecentlyUpdated: () =>
      request<any[]>("/statistics/level0/recently_updated", {
        timeoutMs: 45000,
        retries: 1,
      }),

    level0MostVersions: () =>
      request<any[]>("/statistics/level0/most_versions", {
        timeoutMs: 45000,
        retries: 1,
      }),

    level0Highlights: async () => {
      const empty = {
        top_popular: [] as any[],
        top_recently_updated: [] as any[],
        top_most_versions: [] as any[],
      };

      try {
        const [top_popular, top_most_versions, top_recently_updated] =
          await Promise.all([
            api.stats.level0TopPopular(),
            api.stats.level0MostVersions(),
            api.stats.level0RecentlyUpdated(),
          ]);
        return { top_popular, top_most_versions, top_recently_updated };
      } catch (err) {
        console.error("[stats.level0Highlights] failed", err);
        return empty;
      }
    },

    homeBundle: async (): Promise<
      CachedHomePayload & {
        level0Highlights: {
          top_popular: any[];
          top_recently_updated: any[];
          top_most_versions: any[];
        };
      }
    > => {
      const emptyHome: CachedHomePayload = {
        overview: {},
        summaries: {},
        summaries_top10: {},
        percentages: {},
      };

      const emptyHighlights = {
        top_popular: [] as any[],
        top_recently_updated: [] as any[],
        top_most_versions: [] as any[],
      };

      let home = emptyHome;
      let level0Highlights = emptyHighlights;

      try {
        home = await api.stats.cached.home();
      } catch (err) {
        console.error("[stats.homeBundle] failed to load cached.home", err);
      }

      try {
        level0Highlights = await api.stats.level0Highlights();
      } catch (err) {
        console.error(
          "[stats.homeBundle] failed to load level0Highlights",
          err
        );
      }

      return {
        overview: home.overview ?? {},
        summaries: home.summaries ?? {},
        summaries_top10: home.summaries_top10 ?? {},
        percentages: home.percentages ?? {},
        level0Highlights,
      };
    },

    cached: {
      overview: () =>
        request<OverviewStats>("/statistics/cached/overview", {
          timeoutMs: 45000,
          retries: 2,
        }),

      summaries: () =>
        request<SummariesMap>("/statistics/cached/summaries", {
          timeoutMs: 45000,
          retries: 2,
        }),

      percentages: () =>
        request<PercentagesMap>("/statistics/cached/percentages", {
          timeoutMs: 45000,
          retries: 2,
        }),

      home: () =>
        request<CachedHomePayload>("/statistics/cached/home", {
          timeoutMs: 45000,
          retries: 2,
        }),
    },
  },
};

(api as any).__base = API_BASE_URL;

export function setApiBase(next: string) {
  (api as any).__base = next.replace(/\/+$/, "");
}

export async function ping(path: string = "/statistics/overview") {
  const url = `${(api as any).__base ?? API_BASE_URL}${path}`;
  try {
    let res = await fetch(url, { method: "HEAD" });
    if (!res.ok)
      res = await fetch(url, {
        method: "GET",
        headers: { Accept: "application/json" },
      });
    return res.ok;
  } catch {
    return false;
  }
}

export function isAbortIgnore(err: unknown): boolean {
  if (!err || typeof err !== "object") return false;
  const anyErr: any = err;
  if (anyErr.code === "ABORTED_IGNORE") return true;
  const name = String(anyErr.name || "").toLowerCase();
  const msg = String(anyErr.message || "").toLowerCase();
  return (
    name === "aborterror" ||
    msg.includes("timeout") ||
    msg.includes("aborted") ||
    msg.includes("canceled") ||
    msg.includes("cancelled")
  );
}

export type Api = typeof api;
